import './form.scss';

import { Dropzone } from 'dropzone';
import { checkAndLoadFiles, createElementFromHTML, getOffset, smoothScrollTo } from '../../utilities/js/helper';

class FormHandler {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-form',
            buttonDisabledClass: 'button--disabled',
            scrollOffset: 32
        };

        this.settings = Object.assign({}, defaults, options);
        this.$form = $element;
        this.$header = document.querySelector('[data-siteheader="root"]');
        this.$loadingSpinner = createElementFromHTML(window.OPTIONS.loader);
        this.$dropzone = null;
        this.$uploadFiles = this.$form.querySelector('[data-upload-hidden]');
        this.$button = null;
        Dropzone.autoDiscover = false;

        this.initialize();
    }

    initialize () {
        this.initForm();
    }

    initForm () {
        const $form = this.$form.querySelector('form');
        this.$button = this.$form.querySelector('button[type="submit"]');
        this.$dropzone = this.$form.querySelector('[data-dropzone="root"]');

        $form.onsubmit = (e) => {
            e.preventDefault();

            this.submitForm($form);
        };

        if ($form && this.$button) {
            this.$button.onclick = () => {
                this.submitForm($form);
            };
        }

        if (this.$dropzone) {
            this.initDropzone(this.$dropzone);
        }
    }

    initDropzone ($dropzone) {
        const $field = $dropzone.closest('.form-field');
        let dropzone = null;
        let $uploadHidden = this.$form.querySelector('[data-upload-hidden]');
        let uploadedFilesData = [];

        const generateErrorMessage = (add, file, message) => {
            const $$errorMessages = $field.querySelectorAll('.form-errors');

            if ($$errorMessages.length > 0) {
                $$errorMessages.forEach($errorMessage => {
                    if (!$errorMessage.hasAttribute('data-dropzone-file')) {
                        $errorMessage.remove();
                    }
                });
            }

            if (add) {
                this.$errorMessageWrapper = document.createElement('span');
                this.$errorMessageWrapper.classList.add('form-errors');
                this.$errorMessageWrapper.classList.add('form-errors__list');
                this.$errorMessageWrapper.setAttribute('data-dropzone-file', file.name);
                this.$errorMessageWrapper.textContent = typeof message === 'object' ? message.title : message;
                $field.classList.add('form-field--error');
                $field.appendChild(this.$errorMessageWrapper);
            } else {
                const $currentErrorWrapper = $field.querySelector(`[data-dropzone-file="${file.name}"]`);

                if ($currentErrorWrapper) {
                    $currentErrorWrapper.remove();
                }

                if ($field.classList.contains('form-field--error')) {
                    $field.classList.remove('form-field--error');
                }
            }
        };

        dropzone = new Dropzone($dropzone, { // eslint-disable-line
            url: $dropzone.dataset.dropzoneUploadPath,
            addRemoveLinks: true,
            dictDefaultMessage: '',
            dictInvalidFileType: window.TRANSLATIONS.DROPZONE.dictInvalidFileType,
            dictFileTooBig: `${window.TRANSLATIONS.DROPZONE.dictFileTooBig} ({{filesize}} MB, ${window.TRANSLATIONS.DROPZONE.dictFileMaxFileSize} {{maxFilesize}} MB)`,
            dictResponseError: `${window.TRANSLATIONS.DROPZONE.dictResponseError} {{statusCode}}`,
            dictCancelUpload: window.TRANSLATIONS.DROPZONE.dictCancelUpload,
            dictRemoveFile: window.TRANSLATIONS.DROPZONE.dictRemoveFile,
            uploadMultiple: true,
            clickable: '.form-input--dropzone',
            acceptedFiles: '.pdf,.doc,.docx,.jpeg,.jpg,.png',
            maxFilesize: 10,
            init: function () {
                this.on('success', (file, response) => {
                    if (response.files && response.files.length > 0) {
                        response.files.forEach((uploadedFile) => {
                            if (!uploadedFilesData.includes(uploadedFile.path)) {
                                uploadedFilesData.push(uploadedFile.path);
                            }
                        });
                        $uploadHidden.value = JSON.stringify(uploadedFilesData);
                    }
                    generateErrorMessage(false, file);
                });
                this.on('error', (file, error) => {
                    generateErrorMessage(true, file, error);
                });
                this.on('processing', (file) => {
                    file.previewElement.addEventListener('click', () => {
                        console.log('processing', file);
                    });
                });
                this.on('removedfile', (file) => {
                    generateErrorMessage(false, file);
                });
            }
        });
    }

    createErrorContainer (global = false) {
        const $errorMessages = document.createElement('div');
        $errorMessages.classList.add('form-errors');

        if (global === true) {
            $errorMessages.classList.add('form-errors--grouped');
            $errorMessages.setAttribute('aria-live', 'assertive');
            $errorMessages.setAttribute('tabindex', 0);
            $errorMessages.setAttribute('role', 'alert');
        }

        const $errorList = document.createElement('ul');
        $errorList.classList.add('form-errors__list');

        $errorMessages.appendChild($errorList);

        const errorObject = {};
        errorObject.$errorMessages = $errorMessages;
        errorObject.$errorList = $errorList;

        return errorObject;
    }

    generateFormErrors ($form) {
        const errorObject = this.createErrorContainer(true);
        const $errorMessages = errorObject.$errorMessages;
        const $errorList = errorObject.$errorList;

        $errorMessages.appendChild($errorList);

        const $$errorMessages = $form.querySelectorAll('.form-errors');

        $$errorMessages.forEach($errorMessage => {
            const fieldId = $errorMessage.dataset.formId;
            const fieldLabel = $errorMessage.dataset.formLabel;

            if (fieldId && fieldLabel) {
                const $errorItem = document.createElement('li');
                $errorItem.classList.add('form-errors__list-item');
                const $field = `<a href="#${fieldId}"><b>${fieldLabel}</b>: ${$errorMessage.innerText}</a>`;

                $errorItem.innerHTML = $field;
                $errorList.appendChild($errorItem);
            }
        });

        this.$form.insertBefore($errorMessages, this.$form.firstElementChild);

        smoothScrollTo(0, getOffset(this.$form).top - this.scrollOffset, 400);
    }

    submitForm ($form) {
        const formMethod = $form.getAttribute('method');
        let formAction = $form.getAttribute('action');
        const formData = new FormData($form);

        if (!formAction) {
            formAction = location.href;
        }

        if (this.$button !== null) {
            this.$button.appendChild(this.$loadingSpinner);
            this.$button.setAttribute('disabled', true);
        }

        fetch(formAction, {
            method: formMethod,
            body: formData,
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
        })
            .then(async (response) => {
                const status = response.status;
                if ((status >= 200 && status < 300) || status === 422) {
                    const resultData = await response.text();
                    let result = {};

                    if (resultData) {
                        result = JSON.parse(resultData);

                        if (result.content !== undefined) {
                            if (result.redirect === true) {
                                window.location.href = result.content;
                            } else if (result.redirect === false) {
                                const resultData = result.content;
                                const $result = createElementFromHTML(resultData);
                                const resultContent = $result.innerHTML;
                                const $resultContent = createElementFromHTML(resultContent);

                                if ($resultContent) {
                                    const $formInResponse = $resultContent.querySelector('[data-form="root"]');

                                    if ($formInResponse) {
                                        this.$form.innerHTML = '';
                                        this.$form.innerHTML = $formInResponse.innerHTML;
                                        this.initForm();
                                    } else {
                                        this.$form.replaceWith($resultContent);
                                    }

                                    this.$form.scrollIntoView({ behavior: 'smooth', block: 'start' });

                                    this.generateFormErrors(this.$form);
                                    checkAndLoadFiles($result);

                                    const formSubmittedEvent = new CustomEvent('form.submitted', {
                                        view: window,
                                        bubbles: true,
                                        cancelable: false,
                                        detail: {
                                            $context: this.$form
                                        }
                                    });
                                    window.dispatchEvent(formSubmittedEvent);

                                    mtcaptchaConfig.renderQueue.push('mtcaptcha-job-application');
                                }
                            }
                        }
                    }
                } else {
                    throw new Error(`Unexpected status code: ${status}`);
                }
            });
    }
}

export { FormHandler };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$form = $context.querySelectorAll('[data-form="root"]');
        for (let i = 0; i < $$form.length; i++) {
            const $form = $$form[i];

            $form.API = new FormHandler($form);
        }
    }
});
